import { i18n } from "@tellescope/i18n";
import { Tool, ToolSelection } from "./tools";
import { AgentError } from "./types";

/**
 * Strips markdown code block syntax from text.
 * Handles ```json, ```typescript, ``` etc.
 * Returns the inner content if a code block is found, otherwise returns original text.
 */
export const stripMarkdownCodeBlock = (text: string): string => {
  const trimmed = text.trim();

  // Match ```language\n...\n``` or ```\n...\n```
  const codeBlockRegex = /^```(?:\w*)\n?([\s\S]*?)\n?```$/;
  const match = trimmed.match(codeBlockRegex);
  const matchVal = match ? match[1] : undefined;

  if (matchVal) {
    return matchVal.trim();
  }

  return text;
};

// call onError rather than throwing and losing type safety to "any" Error
export const validate_tool_selection = (
  selection: unknown,
  tools: Tool[],
  onError?: (error: AgentError) => void
): ToolSelection[] => {
  if (!Array.isArray(selection)) {
    onError?.({ message: i18n.t("Tool selection must be an array", { ns: "ai-agent" }) });
    return [];
  }

  const validatedSelections: ToolSelection[] = [];

  for (let index = 0; index < selection.length; index++) {
    const item = selection[index];

    if (typeof item !== "object" || item === null) {
      onError?.({ message: i18n.t("Tool selection at index {{index}} must be an object", { ns: "ai-agent", index }) });
      continue;
    }

    const { t, i } = item as { t: unknown; i: unknown };

    if (typeof t !== "string" || typeof i !== "string") {
      onError?.({
        message: i18n.t("Tool selection at index {{index}} must have valid format", { ns: "ai-agent", index }),
      });
      continue;
    }

    const tool = tools.find((tool) => tool.name === t);
    if (!tool) {
      onError?.({
        message: i18n.t("Tool '{{toolName}}' at index {{index}} is not recognized", {
          ns: "ai-agent",
          toolName: t,
          index,
        }),
      });
      continue;
    }

    validatedSelections.push({ toolName: t, instructions: i });
  }

  return validatedSelections;
};

/**
 * Extracts an array from parsed JSON, handling both direct arrays and wrapped objects.
 * If the input is already an array, returns it directly.
 * If the input is an object, returns the first array value found.
 * Sometimes LLMs will nest an array inside an object with a non-deterministic key
 */
export const extract_array_from_json = (parsed: unknown): unknown[] | null => {
  if (Array.isArray(parsed)) {
    return parsed;
  }

  if (parsed && typeof parsed === "object") {
    // Look for the first array value in the object
    for (const value of Object.values(parsed)) {
      if (Array.isArray(value)) {
        return value;
      }
    }
  }

  return null;
};

export const find_and_parse_json_output = (text: string): object | unknown[] | null => {
  // Strip markdown code blocks first
  const cleanedText = stripMarkdownCodeBlock(text);

  // Find first occurrence of { or [
  const objectStart = cleanedText.indexOf("{");
  const arrayStart = cleanedText.indexOf("[");

  // Determine which JSON type appears first (or only)
  let jsonStart: number;
  let jsonEnd: number;

  if (objectStart === -1 && arrayStart === -1) {
    return null;
  } else if (objectStart === -1) {
    jsonStart = arrayStart;
    jsonEnd = cleanedText.lastIndexOf("]");
  } else if (arrayStart === -1) {
    jsonStart = objectStart;
    jsonEnd = cleanedText.lastIndexOf("}");
  } else {
    // Both exist - use whichever comes first
    if (objectStart < arrayStart) {
      jsonStart = objectStart;
      jsonEnd = cleanedText.lastIndexOf("}");
    } else {
      jsonStart = arrayStart;
      jsonEnd = cleanedText.lastIndexOf("]");
    }
  }

  if (jsonEnd > jsonStart) {
    const jsonString = cleanedText.substring(jsonStart, jsonEnd + 1);
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  }

  return null;
};
